import {
  createContext,
  Fragment,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { classNames } from 'utils';

type SectionEntry = { id: string; label: string };

type ModalContextValue = {
  registerSection: (entry: SectionEntry) => void;
  unregisterSection: (id: string) => void;
  setActiveSectionId: (id: string) => void;
  sections: SectionEntry[];
  activeSectionId: string | null;
};

const ModalContext = createContext<ModalContextValue | null>(null);

function useModalContext(consumer: string): ModalContextValue {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error(`<Modal.${consumer}> must be rendered inside <Modal>`);
  return ctx;
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  ariaLabel: string;
  children: ReactNode;
  forceAction?: boolean;
  onSectionChange?: (sectionId: string) => void;
}

function Modal({ isOpen, onClose, ariaLabel, children, forceAction, onSectionChange }: ModalProps) {
  const [sections, setSections] = useState<SectionEntry[]>([]);
  const [activeSectionId, setActiveSectionIdState] = useState<string | null>(null);

  const registerSection = useCallback((entry: SectionEntry) => {
    setSections((prev) => (prev.find((s) => s.id === entry.id) ? prev : [...prev, entry]));
  }, []);

  const unregisterSection = useCallback((id: string) => {
    setSections((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const setActiveSectionId = useCallback((id: string) => {
    setActiveSectionIdState(id);
  }, []);

  useEffect(() => {
    if (activeSectionId) onSectionChange?.(activeSectionId);
  }, [activeSectionId, onSectionChange]);

  const ctx = useMemo<ModalContextValue>(
    () => ({ registerSection, unregisterSection, setActiveSectionId, sections, activeSectionId }),
    [registerSection, unregisterSection, setActiveSectionId, sections, activeSectionId],
  );

  const handleClose = () => {
    if (!forceAction) onClose();
  };

  return (
    <ModalContext.Provider value={ctx}>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleClose} aria-label={ariaLabel}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-40" aria-hidden="true" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center md:items-center md:p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
                enterTo="opacity-100 translate-y-0 md:scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 md:scale-100"
                leaveTo="opacity-0 md:scale-95"
              >
                <Dialog.Panel
                  className={classNames(
                    'relative w-full bg-white dark:bg-slate-800 shadow-xl dark:shadow-slate-900/50 transition-all',
                    'flex flex-col md:flex-row',
                    'h-[100dvh] md:h-[90vh] md:max-w-[1100px] md:rounded-2xl overflow-hidden',
                  )}
                >
                  {!forceAction && (
                    <button
                      type="button"
                      onClick={onClose}
                      aria-label="Close"
                      className={classNames(
                        'absolute top-3 right-3 z-20 w-8 h-8 flex items-center justify-center rounded-full',
                        'bg-white/90 text-gray-700 hover:bg-gray-100',
                        'dark:bg-slate-700/90 dark:text-gray-300 dark:hover:bg-slate-600',
                      )}
                    >
                      ×
                    </button>
                  )}
                  {children}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </ModalContext.Provider>
  );
}

function ModalMain({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={classNames(
        // min-h-0 lets the grid/flex item shrink below content height so overflow-y-auto kicks in.
        'flex-1 min-h-0 min-w-0 overflow-y-auto p-5 md:p-8 pb-32 md:pb-8',
        className ?? '',
      )}
    >
      {children}
    </div>
  );
}

function ModalRail({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <aside
      className={classNames(
        'hidden md:flex md:flex-col md:w-[320px] md:flex-none md:min-h-0 md:overflow-y-auto md:p-6',
        'md:border-l md:border-stone md:bg-accent_stone',
        'dark:md:border-slate-700 dark:md:bg-slate-700',
        className ?? '',
      )}
    >
      {children}
    </aside>
  );
}

function ModalMobileActionBar({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={classNames(
        'md:hidden fixed bottom-0 inset-x-0 z-30',
        'bg-white dark:bg-slate-800 border-t border-stone dark:border-slate-700',
        'px-4 py-3 pb-[calc(env(safe-area-inset-bottom)+12px)]',
        className ?? '',
      )}
    >
      {children}
    </div>
  );
}

function ModalAnchorNav({ className }: { className?: string }) {
  const { sections, activeSectionId } = useModalContext('AnchorNav');

  const handleClick = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <nav
      aria-label="Section navigation"
      className={classNames(
        'flex gap-5 overflow-x-auto whitespace-nowrap text-14',
        'border-b border-stone dark:border-slate-700 mb-4',
        className ?? '',
      )}
    >
      {sections.map((s) => {
        const isActive = activeSectionId === s.id;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => handleClick(s.id)}
            aria-current={isActive ? 'true' : undefined}
            className={classNames(
              'pb-2 -mb-px transition-colors',
              isActive
                ? 'text-vine dark:text-white font-semibold border-b-2 border-orange'
                : 'text-gray-500 dark:text-gray-400 hover:text-vine dark:hover:text-white',
            )}
          >
            {s.label}
          </button>
        );
      })}
    </nav>
  );
}

interface ModalSectionProps {
  id: string;
  label: string;
  title?: ReactNode;
  children: ReactNode;
  className?: string;
}

function ModalSection({ id, label, title, children, className }: ModalSectionProps) {
  const { registerSection, unregisterSection, setActiveSectionId } = useModalContext('Section');
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    registerSection({ id, label });
    return () => unregisterSection(id);
  }, [id, label, registerSection, unregisterSection]);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveSectionId(id);
        });
      },
      { rootMargin: '-25% 0px -55% 0px', threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [id, setActiveSectionId]);

  return (
    <section
      ref={ref}
      id={id}
      aria-labelledby={title ? `${id}-title` : undefined}
      className={classNames('py-5 border-b border-stone dark:border-slate-700 last:border-0', className ?? '')}
    >
      {title && (
        <h3 id={`${id}-title`} className="text-base font-semibold text-vine dark:text-white mb-3">
          {title}
        </h3>
      )}
      {children}
    </section>
  );
}

Modal.Main = ModalMain;
Modal.Rail = ModalRail;
Modal.MobileActionBar = ModalMobileActionBar;
Modal.AnchorNav = ModalAnchorNav;
Modal.Section = ModalSection;

export default Modal;
